import apiClient from './apiClient';
import Task from '../models/Task';

class TaskService {
  async getTasks(params = {}) {
    try {
      // Convert params to query parameters
      const queryParams = {};
      
      // Add taskType if provided
      if (params.taskType) {
        queryParams.taskType = params.taskType;
      }
      
      // Add pagination params if provided
      if (params.page) {
        queryParams.page = params.page;
      }
      
      if (params.limit) {
        queryParams.limit = params.limit;
      }
      
      const response = await apiClient.get('/tasks', queryParams);
      //console.log("Response received in getTasks:", response);
      if (!response || !response.tasks) {
        console.error("Invalid response structure received:", response);
        throw new Error("Invalid response structure received from API");
      }
      return {
        tasks: response.tasks.map(Task.fromJson),
        pagination: response.pagination || {
          current_page: 1,
          total_pages: 1,
          total_items: response.tasks.length,
          limit: response.tasks.length,
          has_next_page: false,
          has_previous_page: false
        }
      };
    } catch (error) {
      console.error('Error caught in TaskService.getTasks:', error);
      console.error('Error fetching tasks:', error);
      throw error;
    }
  }

  async getTaskById(taskId) {
    try {
      const response = await apiClient.get(`/tasks/${taskId}`);
      return Task.fromJson(response);
    } catch (error) {
      console.error(`Error fetching task with ID ${taskId}:`, error);
      throw error;
    }
  }

  async createTask(taskData) {
    try {
      // Format the data based on the schedule type
      const formattedData = { ...taskData };
      
      switch (formattedData.task_type) {
        case 'Scheduled':
          // Ensure required fields for scheduled tasks
          if (!formattedData.scheduled_date) {
            throw new Error('Scheduled date is required for Scheduled tasks');
          }
          break;
        case 'Recurring':
          // Ensure required fields for recurring tasks
          if (!formattedData.recurring_formula) {
            throw new Error('Recurring formula is required for Recurring tasks');
          }
          break;
        case 'ThisWeek':
        case 'Waitlist':
          // These types don't require special fields
          break;
        default:
          // Default to ThisWeek if no valid type is specified
          formattedData.task_type = 'ThisWeek';
      }
      
      const response = await apiClient.post('/tasks', formattedData);
      return Task.fromJson(response);
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }

  async updateTask(taskId, taskData) {
    try {
      const response = await apiClient.put(`/tasks/${taskId}`, taskData);
      return Task.fromJson(response);
    } catch (error) {
      console.error(`Error updating task with ID ${taskId}:`, error);
      throw error;
    }
  }

  async deleteTask(taskId) {
    try {
      await apiClient.delete(`/tasks/${taskId}`);
      return true;
    } catch (error) {
      console.error(`Error deleting task with ID ${taskId}:`, error);
      throw error;
    }
  }

  async completeTask(taskId) {
    try {
      const response = await apiClient.put(`/tasks/${taskId}/complete`);
      return Task.fromJson(response);
    } catch (error) {
      console.error(`Error completing task with ID ${taskId}:`, error);
      throw error;
    }
  }

  async reopenTask(taskId) {
    try {
      const response = await apiClient.put(`/tasks/${taskId}/reopen`);
      return Task.fromJson(response);
    } catch (error) {
      console.error(`Error reopening task with ID ${taskId}:`, error);
      throw error;
    }
  }

  async addSubtask(taskId, subtaskTitle) {
    try {
      const response = await apiClient.post(`/tasks/${taskId}/subtasks`, {
        title: subtaskTitle
      });
      return Task.fromJson(response);
    } catch (error) {
      console.error(`Error adding subtask to task ${taskId}:`, error);
      throw error;
    }
  }

  async updateSubtask(taskId, subtaskId, updates) {
    try {
      const response = await apiClient.put(`/tasks/${taskId}/subtasks/${subtaskId}`, updates);
      return Task.fromJson(response);
    } catch (error) {
      console.error(`Error updating subtask ${subtaskId}:`, error);
      throw error;
    }
  }

  async deleteSubtask(taskId, subtaskId) {
    try {
      const response = await apiClient.delete(`/tasks/${taskId}/subtasks/${subtaskId}`);
      return Task.fromJson(response);
    } catch (error) {
      console.error(`Error deleting subtask ${subtaskId}:`, error);
      throw error;
    }
  }

  async getCategories() {
    try {
      const response = await apiClient.get('/tasks/categories');
      // Make sure we return an array of categories
      return Array.isArray(response) ? response : 
        (response?.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      return []; // Return empty array on error instead of throwing
    }
  }

  async createCategory(categoryData) {
    try {
      const response = await apiClient.post('/tasks/categories', categoryData);
      return response;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  async updateCategory(categoryId, categoryData) {
    try {
      const response = await apiClient.put(`/tasks/categories/${categoryId}`, categoryData);
      return response;
    } catch (error) {
      console.error(`Error updating category with ID ${categoryId}:`, error);
      throw error;
    }
  }

  async deleteCategory(categoryId) {
    try {
      await apiClient.delete(`/tasks/categories/${categoryId}`);
      return true;
    } catch (error) {
      console.error(`Error deleting category with ID ${categoryId}:`, error);
      throw error;
    }
  }

  async connectNoteToTask(taskId, noteId) {
    try {
      await apiClient.put(`/tasks/connect-note/${taskId}`, { note_id: noteId });
      return true;
    } catch (error) {
      console.error(`Error connecting note ${noteId} to task ${taskId}:`, error);
      throw error;
    }
  }

  async disconnectNoteFromTask(taskId) {
    try {
      await apiClient.delete(`/tasks/disconnect-note/${taskId}`);
      return true;
    } catch (error) {
      console.error(`Error disconnecting note from task ${taskId}:`, error);
      throw error;
    }
  }

  async searchTasks(query) {
    try {
      const response = await apiClient.get(`/tasks/search?q=${encodeURIComponent(query)}`);
      if (!response) {
        console.error("Invalid response structure received from search API");
        throw new Error("Invalid response structure received from API");
      }
      
      const tasks = Array.isArray(response.tasks) 
        ? response.tasks.map(Task.fromJson)
        : (response.tasks || []).map(Task.fromJson);
        
      return {
        tasks,
        pagination: response.pagination || {
          current_page: 1,
          total_pages: 1,
          total_items: tasks.length,
          limit: tasks.length,
          has_next_page: false,
          has_previous_page: false
        }
      };
    } catch (error) {
      console.error(`Error searching tasks with query "${query}":`, error);
      throw error;
    }
  }

  async createTaskFromString(inputString) {
    try {
      const response = await apiClient.post('/tasks/create_from_string', {
        input_string: inputString
      });
      return Task.fromJson(response);
    } catch (error) {
      console.error(`Error creating task from string "${inputString}":`, error);
      throw error;
    }
  }

  async toggleTaskCompletion(taskId) {
    try {
      const response = await apiClient.put(`/tasks/toggle-completion/${taskId}`);
      return Task.fromJson(response);
    } catch (error) {
      console.error(`Error toggling completion status for task ${taskId}:`, error);
      throw error;
    }
  }

  async getTasksByDay(date) {
    try {
      const formattedDate = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      const response = await apiClient.get(`/tasks/day/${formattedDate}`);
      
      if (!response || !response.tasks) {
        console.error("Invalid response structure received:", response);
        throw new Error("Invalid response structure received from API");
      }
      
      return {
        tasks: Array.isArray(response.tasks) 
          ? response.tasks.map(Task.fromJson) 
          : [],
        date: formattedDate
      };
    } catch (error) {
      console.error(`Error fetching tasks for date ${date}:`, error);
      throw error;
    }
  }
}

export default new TaskService(); 